import React, { useState, useEffect } from 'react';
import { Bell, AlertTriangle, Megaphone, Info, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../api';

const PRIORITY = {
  urgent:  { label: 'Urgent',  bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    dot: 'bg-red-500',    icon: AlertTriangle },
  high:    { label: 'High',    bg: 'bg-amber-50',   border: 'border-amber-200',  text: 'text-amber-700',  dot: 'bg-amber-500',  icon: Megaphone },
  normal:  { label: 'Normal',  bg: 'bg-blue-50',    border: 'border-blue-200',   text: 'text-blue-700',   dot: 'bg-blue-400',   icon: Bell },
  low:     { label: 'Low',     bg: 'bg-slate-50',   border: 'border-slate-200',  text: 'text-slate-600',  dot: 'bg-slate-400',  icon: Info },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60)   return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function AnnouncementCard({ ann }) {
  const [open, setOpen] = useState(false);
  const cfg = PRIORITY[ann.priority] || PRIORITY.normal;
  const Icon = cfg.icon;

  return (
    <div
      className={`rounded-2xl border ${cfg.border} ${open ? cfg.bg : 'bg-white'} transition-all shadow-sm overflow-hidden`}
      data-testid={`ann-card-${ann.id}`}
    >
      <button
        className="w-full flex items-start gap-3 p-4 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${cfg.bg} border ${cfg.border}`}>
          <Icon className={`w-4 h-4 ${cfg.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-black text-slate-800 leading-tight">{ann.title}</p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} border ${cfg.border} capitalize`}>
                {cfg.label}
              </span>
              {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-xs text-slate-400">{ann.created_by_name || 'School Admin'}</p>
            <span className="text-slate-200">·</span>
            <p className="text-xs text-slate-400">{timeAgo(ann.created_at)}</p>
          </div>
          {!open && ann.content && (
            <p className="text-xs text-slate-500 mt-1 line-clamp-1">{ann.content}</p>
          )}
        </div>
      </button>
      {open && ann.content && (
        <div className="px-4 pb-4 ml-12">
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{ann.content}</p>
        </div>
      )}
    </div>
  );
}

export default function StudentAnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/announcements')
      .then(r => { if (r?.success) setAnnouncements(r.data || []); })
      .finally(() => setLoading(false));
  }, []);

  const urgent = announcements.filter(a => a.priority === 'urgent');
  const rest   = announcements.filter(a => a.priority !== 'urgent');

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2">
          <Bell className="w-6 h-6 text-[#2563eb]" /> Announcements
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">Messages and updates from your school</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm py-20 text-center" data-testid="announcements-empty">
          <Bell className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="font-bold text-slate-500">No announcements yet</p>
          <p className="text-sm text-slate-400 mt-1">Your school admin hasn't posted any announcements for students</p>
        </div>
      ) : (
        <>
          {urgent.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-bold text-red-500 uppercase tracking-wide flex items-center gap-1.5">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                Urgent
              </p>
              {urgent.map(a => <AnnouncementCard key={a.id} ann={a} />)}
            </div>
          )}
          {rest.length > 0 && (
            <div className="space-y-3">
              {urgent.length > 0 && (
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">All Notices</p>
              )}
              {rest.map(a => <AnnouncementCard key={a.id} ann={a} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
